import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Chip,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Grid
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import ClassIcon from '@mui/icons-material/Class';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import 'moment/locale/fr';

const GoalDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [notes, setNotes] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [studentProgress, setStudentProgress] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const goalDoc = await getDoc(doc(db, 'objectives', id));
        if (!goalDoc.exists()) {
          navigate('/goals');
          return;
        }
        
        const goalData = goalDoc.data();
        setGoal({
          id: goalDoc.id,
          ...goalData,
          deadline: goalData.deadline?.toDate() || null
        });
        setNotes(goalData.notes || '');

        // --- Récupérer les éléments assignés (cours ou élèves)
        const assignedIds = goalData.assignedTo || [];
        
        if (goalData.assignmentType === 'course') {
          // Charger les cours depuis dance_courses
          const courseDocs = await Promise.all(
            assignedIds.map(cid => getDoc(doc(db, 'dance_courses', cid)))
          );
          const courseItems = courseDocs
            .filter(d => d.exists())
            .map(d => ({ 
              id: d.id, 
              ...d.data(),
              title: d.data().title || 'Cours sans titre'
            }));
          setCourses(courseItems);
        } else {
          // Charger les élèves depuis users
          const userDocs = await Promise.all(
            assignedIds.map(uid => getDoc(doc(db, 'users', uid)))
          );
          const userItems = userDocs
            .filter(d => d.exists())
            .map(d => ({ 
              id: d.id, 
              ...d.data(),
              fullName: `${d.data().firstName || ''} ${d.data().lastName || ''}`.trim() || d.data().email || d.id
            }));
          setStudents(userItems);
        }

        // Normaliser la progression
        const rawProgress = goalData.progress ?? 0;
        const rawStudentProgress = goalData.studentProgress || {};

        // 1) Déduire la progression globale (nombre 0-100)
        let globalProgress = 0;
        let progressMap = {};

        if (typeof rawProgress === 'number') {
          globalProgress = rawProgress;
        } else if (rawProgress && typeof rawProgress === 'object') {
          progressMap = rawProgress;
        }

        // Traiter également studentProgress comme source de vérité
        if (rawStudentProgress && typeof rawStudentProgress === 'object') {
          // Fusionner les deux sources de données
          progressMap = {
            ...progressMap,
            ...Object.fromEntries(
              Object.entries(rawStudentProgress).map(([studentId, progressData]) => {
                return [
                  studentId,
                  typeof progressData === 'object' 
                    ? progressData.status || progressData 
                    : progressData
                ];
              })
            )
          };
        }

        // Calculer la progression globale
        const entries = Object.values(progressMap).map(v =>
          typeof v === 'string' ? v : v?.status
        );
        const total = entries.length;
        const doneCount = entries.filter(s =>
          ['terminé', 'termine', 'done', 'completed', 'complete'].includes(
            String(s).toLowerCase()
          )
        ).length;
        globalProgress = total > 0 ? Math.round((doneCount / total) * 100) : 0;

        // 2) Construire le tableau de progrès par élève
        const progressArray = Object.entries(rawStudentProgress).map(([studentId, progressData]) => {
          const isObject = typeof progressData === 'object';
          return {
            studentId,
            status: isObject ? progressData.status : progressData,
            comment: isObject ? progressData.comment || '' : '',
            updatedAt: isObject ? (progressData.updatedAt?.toDate?.() || new Date()) : new Date(),
            updatedBy: isObject ? progressData.updatedBy || '' : ''
          };
        });

        setStudentProgress(progressArray);

        // Écraser goal.progress par un nombre sûr pour l'UI
        goalData.progress = globalProgress;

        // --- Compléter la liste `students` à partir des IDs du progressMap
        const idsFromProgress = Object.keys(progressMap || {});
        const idsFromAssignIfStudents = goalData.assignmentType === 'student' ? assignedIds : [];
        const neededIds = Array.from(new Set([...idsFromProgress, ...idsFromAssignIfStudents]))
          .filter(Boolean);

        if (neededIds.length) {
          // Si assignation à des cours, on récupère aussi les participants depuis les cours
          let idsFromCourses = [];
          if (goalData.assignmentType === 'course') {
            // Récupérer les étudiants inscrits aux cours assignés
            for (const courseId of assignedIds) {
              try {
                const courseDoc = await getDoc(doc(db, 'dance_courses', courseId));
                if (courseDoc.exists()) {
                  const courseData = courseDoc.data();
                  const enrolledStudents = courseData.enrolledStudents || [];
                  idsFromCourses = [...idsFromCourses, ...enrolledStudents];
                }
              } catch (error) {
                console.error(`Erreur chargement cours ${courseId}:`, error);
              }
            }
          }

          const allNeeded = Array.from(new Set([...neededIds, ...idsFromCourses]));

          // Récupérer les users manquants par rapport à `students` déjà chargés
          const already = new Set(students.map(s => s.id));
          const toFetch = allNeeded.filter(id => !already.has(id));

          if (toFetch.length) {
            const fetchedUserDocs = await Promise.all(
              toFetch.map(uid => getDoc(doc(db, 'users', uid)))
            );
            const fetchedUsers = fetchedUserDocs
              .filter(d => d.exists())
              .map(d => ({ 
                id: d.id, 
                ...d.data(),
                fullName: `${d.data().firstName || ''} ${d.data().lastName || ''}`.trim() || d.data().email || d.id
              }));

            // Fusion/dédupe
            const merged = [
              ...students,
              ...fetchedUsers
            ].reduce((acc, u) => {
              acc.set(u.id, u);
              return acc;
            }, new Map());
            setStudents(Array.from(merged.values()));
          }
        }

      } catch (error) {
        console.error("Erreur de chargement:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleSaveNotes = async () => {
    try {
      await updateDoc(doc(db, 'objectives', id), {
        notes: notes
      });
    } catch (error) {
      console.error("Erreur de sauvegarde:", error);
    }
  };

  const formatDeadline = (date) => {
    if (!date) return 'Aucune deadline';
    return moment(date).format('dddd D MMMM YYYY [à] HH:mm');
  };

  const getDeadlineStatus = (date) => {
    if (!date) return 'Aucun';
    const now = new Date();
    const deadlineDate = new Date(date);
    
    if (deadlineDate < now) return 'Dépassé';
    if (deadlineDate - now < 86400000) return 'Urgent';
    return 'En cours';
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={60} sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  if (!goal) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Objectif non trouvé</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/goals')}
        sx={{ mb: 2 }}
      >
        Retour à la liste
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{goal.title}</Typography>
        <Button
          variant="contained"
          component={Link}
          to={`/goals/edit/${id}`}
        >
          Modifier l'objectif
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Description</Typography>
              <Typography>{goal.description || 'Aucune description'}</Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Détails</Typography>
                <Chip 
                  label={
                    goal.type === 'daily' ? 'Journalier' : 
                    goal.type === 'weekly' ? 'Hebdomadaire' : 
                    goal.type === 'monthly' ? 'Mensuel' : 
                    'Annuel'
                  } 
                  color="primary"
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Type d'assignation:</Typography>
                  <Typography>
                    {goal.assignmentType === 'course' ? 'Cours' : 'Élève'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Deadline:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventIcon fontSize="small" />
                    <Typography>{formatDeadline(goal.deadline)}</Typography>
                    <Chip 
                      label={getDeadlineStatus(goal.deadline)} 
                      color={
                        getDeadlineStatus(goal.deadline) === 'Dépassé' ? 'error' : 
                        getDeadlineStatus(goal.deadline) === 'Urgent' ? 'warning' : 'success'
                      }
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Progression globale:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={typeof goal.progress === 'number' ? goal.progress : 0} 
                      sx={{ flexGrow: 1, height: 8 }}
                    />
                    <Typography>{typeof goal.progress === 'number' ? goal.progress : 0}%</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Notes</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                onClick={handleSaveNotes}
              >
                Enregistrer les notes
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab label="Assignations" />
                <Tab label="Progrès" />
              </Tabs>

              {activeTab === 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {goal.assignmentType === 'course' ? 'Cours assignés' : 'Élèves assignés'}
                  </Typography>
                  <List>
                    {(goal.assignmentType === 'course' ? courses : students).map((item) => (
                      <ListItem key={item.id} sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {goal.assignmentType === 'course' ? <ClassIcon /> : <PersonIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={goal.assignmentType === 'course' ? item.title : item.fullName}
                          secondary={goal.assignmentType === 'course' ? (item.teacherName || 'Professeur non spécifié') : item.email}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {activeTab === 1 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Progrès des élèves
                  </Typography>
                  {studentProgress.length > 0 ? (
                    <List>
                      {studentProgress.map((progress) => {
                        const student = students.find(s => s.id === progress.studentId);
                        const statusStr = String(progress.status || '').toLowerCase();
                        const isDone = ['terminé','termine','done','completed','complete'].includes(statusStr);
                        return (
                          <ListItem key={progress.studentId} sx={{ px: 0 }}>
                            <ListItemAvatar>
                              <Avatar>
                                <PersonIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={student ? student.fullName : progress.studentId}
                              secondary={
                                <Box sx={{ mt: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={isDone ? 100 : 0}
                                    sx={{ mb: 1 }}
                                  />
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    Statut: {isDone ? '✅ Terminé' : '🔄 En cours'}
                                  </Typography>
                                  {progress.comment && (
                                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                      "{progress.comment}"
                                    </Typography>
                                  )}
                                  {progress.updatedBy && (
                                    <Typography variant="caption" display="block">
                                      Mis à jour par {progress.updatedBy} le {moment(progress.updatedAt).format('DD/MM/YYYY')}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Aucun progrès enregistré
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GoalDetails;